/**
 * Mobile Permission Management
 *
 * App Store/Play Store Compliance Strategy:
 * - NEVER request all permissions at once on first launch
 * - Request permissions contextually when the feature is needed
 * - Show explanation before requesting (pre-permission prompt)
 * - Handle denied permissions gracefully
 *
 * Permission Flow:
 * 1. Push Notifications - After successful login (not on app launch)
 * 2. ATT (iOS only) - After login, before analytics
 * 3. Microphone - When user first tries to make a call
 * 4. Camera - When user tries to scan document or take photo
 * 5. Contacts - When user tries to import contacts (optional)
 * 6. Location - When user needs location-based features (optional)
 */

import { Capacitor } from '@capacitor/core';
import { isPushRegistrationSafe } from './push-notifications';

// Permission status types
export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

export interface PermissionState {
  pushNotifications: PermissionStatus;
  microphone: PermissionStatus;
  camera: PermissionStatus;
  contacts: PermissionStatus;
  location: PermissionStatus;
  tracking: PermissionStatus; // iOS ATT
}

// Storage key for tracking permission request history
const PERMISSION_HISTORY_KEY = 'insuracentral_permission_history';

interface PermissionHistory {
  pushNotifications?: { requested: boolean; timestamp: number };
  microphone?: { requested: boolean; timestamp: number };
  camera?: { requested: boolean; timestamp: number };
  tracking?: { requested: boolean; timestamp: number };
}

/**
 * Get permission request history from storage
 */
async function getPermissionHistory(): Promise<PermissionHistory> {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(PERMISSION_HISTORY_KEY);
      return stored ? JSON.parse(stored) : {};
    }
  } catch {
    // Ignore storage errors
  }
  return {};
}

/**
 * Save permission request to history
 */
async function savePermissionRequest(permission: keyof PermissionHistory): Promise<void> {
  try {
    const history = await getPermissionHistory();
    history[permission] = { requested: true, timestamp: Date.now() };
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PERMISSION_HISTORY_KEY, JSON.stringify(history));
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check if we've already requested a permission
 */
export async function hasRequestedPermission(permission: keyof PermissionHistory): Promise<boolean> {
  const history = await getPermissionHistory();
  return !!history[permission]?.requested;
}

/**
 * Request Push Notification Permission
 * Call this AFTER successful login, not on app launch
 */
export async function requestPushNotificationPermission(): Promise<PermissionStatus> {
  if (!Capacitor.isNativePlatform()) {
    return 'granted'; // Web handles this differently
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Check current status first
    const { receive } = await PushNotifications.checkPermissions();

    if (receive === 'granted') {
      return 'granted';
    }

    if (receive === 'denied') {
      return 'denied';
    }

    // Request permission
    const result = await PushNotifications.requestPermissions();
    await savePermissionRequest('pushNotifications');

    if (result.receive === 'granted') {
      // Register for push notifications (guarded: Android crashes natively
      // without Firebase configured — see push-notifications.ts)
      if (isPushRegistrationSafe(Capacitor.getPlatform())) {
        await PushNotifications.register();
      } else {
        console.warn("[push] Android registration skipped — Firebase not configured");
      }
      return 'granted';
    }

    return result.receive as PermissionStatus;
  } catch (error) {
    console.error('Error requesting push permission:', error);
    return 'denied';
  }
}

/**
 * Check Push Notification Permission Status (without requesting)
 */
export async function checkPushNotificationPermission(): Promise<PermissionStatus> {
  if (!Capacitor.isNativePlatform()) {
    return 'granted';
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { receive } = await PushNotifications.checkPermissions();
    return receive as PermissionStatus;
  } catch {
    return 'prompt';
  }
}

/**
 * Request Microphone Permission
 * Call this when user tries to make their FIRST call
 */
export async function requestMicrophonePermission(): Promise<PermissionStatus> {
  if (!Capacitor.isNativePlatform()) {
    // Web uses navigator.mediaDevices
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return 'granted';
    } catch {
      return 'denied';
    }
  }

  try {
    // For native, we need to use a plugin or the web API through Capacitor
    // The Twilio Voice SDK will handle this, but we can pre-check
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    await savePermissionRequest('microphone');
    return 'granted';
  } catch (error: any) {
    console.error('Error requesting microphone permission:', error);
    if (error.name === 'NotAllowedError') {
      return 'denied';
    }
    return 'prompt';
  }
}

/**
 * Check Microphone Permission Status (without requesting)
 */
export async function checkMicrophonePermission(): Promise<PermissionStatus> {
  try {
    // Check using Permissions API if available
    if (navigator.permissions) {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return result.state as PermissionStatus;
    }
  } catch {
    // Permissions API not fully supported
  }

  // Fallback: we don't know, assume prompt
  return 'prompt';
}

/**
 * Request Camera Permission
 * Call this when user tries to take a photo or scan a document
 * Note: Uses browser API as @capacitor/camera is not installed
 */
export async function requestCameraPermission(): Promise<PermissionStatus> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    await savePermissionRequest('camera');
    return 'granted';
  } catch (error) {
    console.error('Error requesting camera permission:', error);
    return 'denied';
  }
}

/**
 * Check Camera Permission Status (without requesting)
 * Note: Uses browser Permissions API as @capacitor/camera is not installed
 */
export async function checkCameraPermission(): Promise<PermissionStatus> {
  try {
    if (navigator.permissions) {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return result.state as PermissionStatus;
    }
  } catch {
    // Permissions API not fully supported
  }
  return 'prompt';
}

/**
 * Get all permission statuses
 */
export async function getAllPermissionStatuses(): Promise<Partial<PermissionState>> {
  const [push, mic, camera] = await Promise.all([
    checkPushNotificationPermission(),
    checkMicrophonePermission(),
    checkCameraPermission(),
  ]);

  return {
    pushNotifications: push,
    microphone: mic,
    camera: camera,
  };
}

/**
 * Open app settings so user can manually enable permissions
 * Note: On native platforms, this will show instructions to the user
 * since deep-linking to app settings requires a native plugin not yet
 * compatible with Capacitor 8.
 */
export async function openAppSettings(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    // Can't open settings from web
    console.log('Cannot open app settings from web');
    return false;
  }

  // On native, we can't directly open settings without capacitor-native-settings
  // which doesn't support Capacitor 8 yet. Return false to indicate the caller
  // should show instructions to the user instead.
  console.log('Direct app settings not available - show manual instructions to user');
  return false;
}
